import { useEffect, useRef } from 'preact/hooks';
import { Check, Crown, Lock, Ticket } from 'lucide-preact';
import type { RewardBundle } from '@vm/shared';
import { buyPremiumPass, claimTier, loadBattlePass } from '../state/actions';
import { itemInfo } from '../state/content';
import { go } from '../state/nav';
import { battlePass, wallet } from '../state/store';
import { fmtCompact } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { AsyncButton, Bar, CoinIcon, confirmDialog, Countdown, ErrorState, GemIcon, OnlineOnly, Price, Screen, Spinner } from '../ui/kit';
import { ItemThumb } from './Store';

function RewardCell(props: { reward: RewardBundle | null; state: 'locked' | 'claimable' | 'claimed' | 'future' | 'premium-locked'; onClaim: () => Promise<void>; premium?: boolean }) {
  const r = props.reward;
  if (!r) return <div class="bp-cell is-empty" />;
  const item = r.items?.[0];
  const info = item ? itemInfo(item.type, item.id) : null;
  return (
    <div class={`bp-cell st-${props.state} ${props.premium ? 'is-premium' : ''} ${info ? `r-${info.rarity}` : ''}`} title={info?.name}>
      <div class="bp-cell-art">
        {item ? (
          <ItemThumb type={item.type} id={item.id} scale={3} />
        ) : r.gems ? (
          <span class="bp-currency">
            <GemIcon size={34} />
            <b>{fmtCompact(r.gems)}</b>
          </span>
        ) : r.coins ? (
          <span class="bp-currency">
            <CoinIcon size={34} />
            <b>{fmtCompact(r.coins)}</b>
          </span>
        ) : r.xp ? (
          <span class="bp-currency xp">
            <b>{fmtCompact(r.xp)}</b>
            <small>XP</small>
          </span>
        ) : null}
      </div>
      <span class="bp-cell-name">{info?.name ?? (r.gems ? 'Gems' : r.coins ? 'Coins' : 'XP Boost')}</span>
      {props.state === 'claimed' && (
        <span class="bp-cell-done">
          <Check size={22} strokeWidth={4} />
        </span>
      )}
      {props.state === 'premium-locked' && (
        <span class="bp-cell-lock">
          <Lock size={18} strokeWidth={3} />
        </span>
      )}
      {props.state === 'claimable' && (
        <AsyncButton class="sm primary bp-claim" onClick={props.onClaim}>
          Claim
        </AsyncButton>
      )}
    </div>
  );
}

function Track() {
  const bp = battlePass.value!;
  const scroller = useRef<HTMLDivElement>(null);
  const claimed = new Set(bp.claimed.map((c) => `${c.tier}:${c.track}`));
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const cur = el.querySelector<HTMLElement>(`[data-tier="${Math.max(1, bp.tier)}"]`);
    if (cur) el.scrollLeft = Math.max(0, cur.offsetLeft - el.clientWidth / 2 + cur.clientWidth / 2);
  }, []);
  const rewards = [...bp.season.rewards].sort((a, b) => a.tier - b.tier);
  return (
    <div class="bp-track-wrap">
      <div class="bp-rowlabels">
        <span class="bp-rowlabel free">FREE</span>
        <span class="bp-rowlabel premium">
          <Crown size={16} /> PREMIUM
        </span>
      </div>
      <div class="bp-track" ref={scroller}>
        {rewards.map((r) => {
          const reached = r.tier <= bp.tier;
          const freeState = !r.free ? 'future' : claimed.has(`${r.tier}:free`) ? 'claimed' : reached ? 'claimable' : 'future';
          const premState = !r.premium ? 'future' : claimed.has(`${r.tier}:premium`) ? 'claimed' : !bp.premium ? 'premium-locked' : reached ? 'claimable' : 'future';
          return (
            <div key={r.tier} class={`bp-col ${reached ? 'is-reached' : ''} ${r.tier === bp.tier ? 'is-current' : ''}`} data-tier={r.tier}>
              <div class="bp-tier">{r.tier}</div>
              <RewardCell reward={r.free} state={freeState} onClaim={() => claimTier(r.tier, 'free')} />
              <RewardCell reward={r.premium} state={premState} premium onClaim={() => claimTier(r.tier, 'premium')} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PassHeader() {
  const bp = battlePass.value!;
  const per = bp.season.xpPerTier;
  const maxed = bp.tier >= bp.season.tiers;
  const inTier = maxed ? per : Math.max(0, bp.xp - bp.tier * per);
  const gems = wallet.value.gems;
  const price = bp.season.premiumPriceGems;
  return (
    <div class="bp-head">
      <div class="bp-season">
        <span class="bp-season-kicker">Season</span>
        <h2>{bp.season.name}</h2>
        <Countdown to={bp.season.endsAt} prefix="Ends in " class="bp-ends" />
      </div>
      <div class="bp-progress">
        <div class="bp-progress-tier">
          <span>Tier</span>
          <b>{bp.tier}</b>
        </div>
        <div class="bp-progress-bar">
          <Bar value={inTier} max={per} tone="purple" label={maxed ? 'Max tier!' : `${inTier} / ${per} XP`} />
          <small class="muted">Earn pass XP by playing matches and finishing quests.</small>
        </div>
      </div>
      {bp.premium ? (
        <div class="bp-premium-on">
          <Crown size={22} /> Premium active
        </div>
      ) : (
        <AsyncButton
          class="gem big bp-buy"
          onClick={async () => {
            if (gems < price) {
              if (await confirmDialog({ title: 'Not enough gems', body: `The premium pass costs ${price} gems. Want to get more?`, confirm: 'Get gems' })) go('store', { tab: 'gems' });
              return;
            }
            const ok = await confirmDialog({
              title: 'Unlock Premium Pass?',
              body: (
                <span>
                  Unlock every premium reward this season for <Price price={price} currency="gems" />
                </span>
              ),
              confirm: 'Unlock',
            });
            if (ok) await buyPremiumPass();
          }}
        >
          <Crown size={20} /> Premium <Price price={price} currency="gems" />
        </AsyncButton>
      )}
    </div>
  );
}

export function BattlePassScreen() {
  const load = useAsync(() => loadBattlePass(), []);
  const bp = battlePass.value;
  return (
    <Screen title="Battle Pass" icon={<Ticket size={26} />} class="battlepass">
      <OnlineOnly feature="The battle pass">
        {!bp ? (
          load.error ? (
            <ErrorState message={load.error} onRetry={load.reload} />
          ) : (
            <Spinner label="Loading the season…" />
          )
        ) : (
          <div class="bpass">
            <PassHeader />
            <Track />
          </div>
        )}
      </OnlineOnly>
    </Screen>
  );
}
